import React from 'react'
import PropTypes from 'prop-types'
import { Box, TextField, Typography, InputAdornment } from '@mui/material'
import MuiAutocomplete from '@mui/material/Autocomplete'
import { Search as SearchIcon } from '@mui/icons-material'
import { StatusChip } from '../atoms'

function AutoCompleteInput({
  options,
  value,
  onChange,
  multiple = false,
  getOptionLabel = (option) => option?.displayName || option?.name || '',
  getOptionKey = (option) => option?.name,
  badges = [],
  renderOptionContent = null,
  showSearchIcon = true,
  textFieldProps = {},
  placeholder = 'Search...',
  noOptionsText = 'No options found',
  className = undefined,
  ...muiProps
}) {
  const resolvedValue = multiple && value === null ? [] : value

  return (
    <MuiAutocomplete
      options={options}
      getOptionLabel={getOptionLabel}
      value={resolvedValue}
      onChange={onChange}
      multiple={multiple}
      renderInput={(params) => {
        const { InputProps: extraInputProps, ...restTextFieldProps } =
          typeof textFieldProps === 'function' ? textFieldProps(params) : textFieldProps
        return (
          <TextField
            {...params}
            variant="outlined"
            placeholder={placeholder}
            {...restTextFieldProps}
            InputProps={{
              ...params.InputProps,
              ...(showSearchIcon && {
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: 'var(--color-text-light)' }} />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }),
              ...extraInputProps,
            }}
          />
        )
      }}
      renderOption={(props, option) => (
        <li {...props} key={getOptionKey(option)}>
          {renderOptionContent ? (
            renderOptionContent(option)
          ) : (
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Typography>{getOptionLabel(option)}</Typography>
              <Box display="flex" style={{ gap: 4 }}>
                {badges.map(({ getCount, label, color = 'info' }) => {
                  const count = getCount(option)
                  if (count <= 0) return null
                  return (
                    <StatusChip
                      key={label}
                      size="small"
                      label={`${count} ${label}`}
                      color={color}
                      variant="soft"
                    />
                  )
                })}
              </Box>
            </Box>
          )}
        </li>
      )}
      className={className}
      noOptionsText={noOptionsText}
      {...muiProps}
    />
  )
}

AutoCompleteInput.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onChange: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  getOptionLabel: PropTypes.func,
  getOptionKey: PropTypes.func,
  renderOptionContent: PropTypes.func,
  showSearchIcon: PropTypes.bool,
  textFieldProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  badges: PropTypes.arrayOf(
    PropTypes.shape({
      getCount: PropTypes.func.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string,
    }),
  ),
  placeholder: PropTypes.string,
  noOptionsText: PropTypes.string,
  className: PropTypes.string,
}

export default AutoCompleteInput
